import React from "react";

import { MenuItem, Weights } from "custom-types/MenuItem";
import { Cannabinoid, Strain } from "custom-types/Strain";
import { sortTopCannabs } from "utils/sortTopCannabs";

import { Modal } from "components/botanic/Modal";
import StarRating from "components/botanic/StarRating";
import ComparisonShoppingTable from "components/ComparisonShoppingTable";
import Image from "components/Image";

type Props = {
  strain: Strain;
  setModalIsVisible: React.Dispatch<React.SetStateAction<boolean>>;
  buttonRef: React.MutableRefObject<null>;
  menuItems: MenuItem[];
  menuItemWeights: Weights;
  initialWeight: string;
  menuItemsLoading: boolean;
};

const ComparisonShoppingModal = ({
  strain,
  setModalIsVisible,
  buttonRef,
  menuItems,
  menuItemWeights,
  initialWeight,
  menuItemsLoading,
}: Props) => {
  const roundedStrainRating =
    strain?.averageRating && Math.round(strain.averageRating * 10) / 10;
  const sortedTopCannabs =
    strain?.cannabinoids && sortTopCannabs(strain?.cannabinoids).slice(0, 2);

  return (
    <Modal
      title="Compare Prices"
      onDismissed={() => setModalIsVisible(false)}
      returnFocusRef={buttonRef}
      width={550}
    >
      <div className="relative">
        <div className="row">
          <div className="col-1/3">
            <Image
              src={(strain.nugImage || strain.stockNugImage) as string}
              alt={`Nug image for ${strain.name}`}
              sizes={[165]}
              ratio="square"
            />
          </div>
          <div className="flex flex-col min-w-0 col-2/3">
            <div>
              {!!strain.phenotype && (
                <div className="inline-block font-bold text-xs bg-leafly-white py-xs px-sm rounded mr-xs">
                  {strain.phenotype}
                </div>
              )}
            </div>
            <div className="my-xs">
              <div className="font-bold text-sm mb-xs underline">
                {strain.name}
              </div>
            </div>
            <div className="mb-xs">
              {roundedStrainRating &&
                strain?.reviewCount &&
                strain.reviewCount > 0 && (
                  <StarRating
                    rating={roundedStrainRating}
                    ratingCount={strain.reviewCount}
                    className="text-xs"
                  />
                )}
            </div>
            <div className="flex mb-xs">
              {sortedTopCannabs &&
                sortedTopCannabs.length > 0 &&
                sortedTopCannabs.map((cannab: Cannabinoid) => {
                  return (
                    <span className="mr-md text-xs" key={cannab.displayName}>
                      {cannab.displayName}{" "}
                      {cannab.percentile50 !== null &&
                      cannab.percentile50 >= 0 ? (
                        `${cannab.percentile50}%`
                      ) : (
                        <>&#8212;</>
                      )}
                    </span>
                  );
                })}
            </div>
          </div>
        </div>
        <div className="font-extrabold text-xs pt-md pb-sm uppercase">
          Choose your amount
        </div>
        <ComparisonShoppingTable
          defaultSort="price"
          initialWeight={initialWeight}
          menuItems={menuItems}
          menuItemsLoading={menuItemsLoading}
          menuItemWeights={menuItemWeights}
          onAddToBagClick={() => setModalIsVisible(false)}
          strainName={strain.name}
          trackingCategory="Multi Option Flower Comparison"
        />
        <div className="mt-xl mb-xs text-center">
          <button
            className="underline font-bold"
            onClick={() => setModalIsVisible(false)}
          >
            cancel
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ComparisonShoppingModal;
